import { useEffect, useRef, useState } from 'preact/hooks';
import type { AppRole } from '../../shared/roles';
import type { SearchBody, SearchHit } from '../../shared/search/search';
import { useI18n, type MessageKey } from '../i18n';
import { apiGet } from '../lib/api';
import { statusKey } from './StatusPill';

// The header search box (D99): type a request, PO, voucher, delivery note or petty cash number
// and jump to its request. Combobox pattern (WAI-ARIA APG): arrows move, Enter opens, Esc closes;
// "/" anywhere outside a field puts the cursor in the box.

type State =
  { kind: 'idle' } | { kind: 'loading' } | { kind: 'error' } | { kind: 'done'; body: SearchBody };

const DEBOUNCE_MS = 300;
const STAFF: readonly AppRole[] = ['Procurement', 'CFO', 'Finance'];

export function HeaderSearch({ roles }: { roles: readonly AppRole[] }) {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const staff = roles.some((r) => STAFF.includes(r));

  useEffect(() => {
    const text = q.trim();
    if (!text) {
      setState({ kind: 'idle' });
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      setState({ kind: 'loading' });
      apiGet<SearchBody>(`/api/search?q=${encodeURIComponent(text)}`)
        .then((body) => {
          if (!live) return;
          setState({ kind: 'done', body });
          setActive(0);
        })
        .catch(() => live && setState({ kind: 'error' }));
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      e.preventDefault();
      input.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const hits: SearchHit[] = state.kind === 'done' ? state.body.hits : [];
  const go = (hit: SearchHit | undefined) => {
    if (!hit) return;
    location.hash = `#/requests/${hit.prNo}`;
    setOpen(false);
    setQ('');
    input.current?.blur();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' && hits.length) {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % hits.length);
    } else if (e.key === 'ArrowUp' && hits.length) {
      e.preventDefault();
      setActive((a) => (a - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(hits[active]);
    } else if (e.key === 'Escape') {
      if (open) setOpen(false);
      else setQ('');
    }
  };

  const showPanel = open && q.trim() !== '' && state.kind !== 'idle';
  const message: MessageKey | null =
    state.kind === 'loading'
      ? 'search.loading'
      : state.kind === 'error'
        ? 'search.error'
        : state.kind === 'done' && !state.body.understood
          ? staff
            ? 'search.howStaff'
            : 'search.how'
          : state.kind === 'done' && hits.length === 0
            ? 'search.none'
            : null;

  return (
    <div
      class="header-search"
      onFocusOut={(e) => {
        const next = e.relatedTarget as Node | null;
        if (!next || !(e.currentTarget as HTMLElement).contains(next)) setOpen(false);
      }}
    >
      <label class="visually-hidden" for="header-search">
        {t('search.label')}
      </label>
      <input
        ref={input}
        id="header-search"
        class="header-search__input"
        type="search"
        role="combobox"
        autocomplete="off"
        spellcheck={false}
        maxLength={40}
        dir="ltr"
        placeholder={t(staff ? 'search.placeholderStaff' : 'search.placeholder')}
        aria-expanded={showPanel}
        aria-controls="header-search-list"
        aria-autocomplete="list"
        aria-activedescendant={showPanel && hits[active] ? `hs-${active}` : undefined}
        value={q}
        onInput={(e) => {
          setQ((e.target as HTMLInputElement).value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <div class="header-search__panel" hidden={!showPanel}>
        {message && (
          <p class="header-search__note" role="status">
            {t(message)}
          </p>
        )}
        <ul id="header-search-list" role="listbox" aria-label={t('search.results')}>
          {hits.map((h, i) => (
            // Clicks pick an option; the keyboard path is the combobox's own (APG pattern).
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={`${h.kind}-${h.ref}`}
              id={`hs-${i}`}
              role="option"
              aria-selected={i === active}
              class={`header-search__hit${i === active ? ' header-search__hit--active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(h)}
            >
              <span class="header-search__kind">{t(`search.kind.${h.kind}` as MessageKey)}</span>
              <span class="header-search__ref" dir="ltr">
                {h.ref}
              </span>
              <span class="header-search__title" dir="auto">
                {h.title}
                {h.kind !== 'PR' && (
                  <>
                    {' · '}
                    <span dir="ltr">{h.prNo}</span>
                  </>
                )}
                {' · '}
                {t(statusKey(h.status, !staff))}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
